import moment from 'moment'
import React, { useContext, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import { ReactComponent as StethoscopeIcon } from '../../../assets/icons/stethoscope.svg'
import BackButton from '../../../components/BackButton'
import Card from '../../../components/Card'
import Alert from '../../../components/Form/Alert'
import { Generic as CancelModal } from '../../../components/Modals'
import { RootContext } from '../../../context/rootContext'
import { useGetAppointment } from '../../../hooks/useGetAppointment'
import { useGetPet } from '../../../hooks/useGetPet'
import useGoTo from '../../../hooks/useGoTo'
import { createAppointment, destroyAppointment, updateAppointment } from '../../../services/appointments'
import { DatePicker, Textarea } from '../components/Form'
import PetInfo from '../components/PetsDataCard'
import SideMenuTemplate from '../templates/SideMenu'
import {
  APPOINTMENT_STATUS,
  ERROR_MESSAGES,
  MODAL_WARNINGS,
  Success,
  URLS,
  getAnamnesisFormFields
} from './constants'

import _ from 'lodash'
import * as S from './styles'

const Appointment = () => {
  const history = useHistory()
  const [showSuccess, setShowSuccess] = useState(false)
  const { globalState } = useContext(RootContext)
  const [state] = globalState
  const [petWasUpdated, setPetWasUpdated] = useState(false)
  const [date, setDate] = useState()
  const [errors, setErrors] = useState({})
  const [initialFields, setInitialFields] = useState({})
  const { register, handleSubmit, watch, setValue } = useForm()
  const { getAppointment, appointment, loading: loadingAppointment } = useGetAppointment()
  const { getPet, pet, loading: loadingPet } = useGetPet()
  const goToPage = useGoTo()
  const { partner } = JSON.parse(localStorage.getItem('userData'))
  const queryParams = new URLSearchParams(window.location.search)
  const appointmentId = queryParams.get('appointmentId')
  const success = queryParams.get('success')
  const petId = appointment?.pet?.id || queryParams.get('petId')
  const partnerId = appointment?.partner?.id || partner
  const appointmentStatus = APPOINTMENT_STATUS[!!appointmentId]
  const isEditing = appointmentStatus === 'isEditing'
  const watchedFields = {
    mainComplaint: watch('mainComplaint'),
    physicalExam: watch('physicalExam'),
    diagnosis: watch('diagnosis'),
    treatment: watch('treatment'),
    observation: watch('observation'),
    returnObservation: watch('returnObservation')
  }
  const professionalId = state.userData.professional.id
  const isEditingSameProfessional = isEditing && professionalId !== appointment?.professional.id

  useEffect(() => {
    if (isEditing) {
      handleGetAppointment(appointmentId)
    }
    if (petId) getPet(petId)
  }, [appointmentStatus, petId])

  const ANAMNESIS_FIELDS = getAnamnesisFormFields(appointment)

  const handleGetAppointment = async (id) => {
    await getAppointment(id)
  }

  useEffect(() => {
    if (appointment) {
      setValue('mainComplaint', appointment.mainComplaint)
      setValue('physicalExam', appointment.physicalExam)
      setValue('diagnosis', appointment.diagnosis)
      setValue('treatment', appointment.treatment)
      setValue('observation', appointment.observation)
      setValue('returnObservation', appointment.returnObservation)
    }
    setInitialFields({
      mainComplaint: appointment?.mainComplaint || '',
      physicalExam: appointment?.physicalExam || '',
      diagnosis: appointment?.diagnosis || '',
      treatment: appointment?.treatment || '',
      observation: appointment?.observation || '',
      returnObservation: appointment?.returnObservation || ''
    })
  }, [appointment, setValue])

  useEffect(() => {
    if (petWasUpdated && petId) getPet(petId)
  }, [petWasUpdated])

  const handleSubmitForm = async (form) => {
    const errorsList = {}
    const checkFor = {
      dateRequired: {
        field: 'date',
        isValid: !!date,
        message: ERROR_MESSAGES.requiredField
      },
      dateIsValid: {
        field: 'date',
        isValid: moment(date).isValid(),
        message: ERROR_MESSAGES.invalidDate
      }
    }

    if (!isEditing) {
      Object.values(checkFor).forEach((filled) => {
        if (!filled.isValid) {
          errorsList[filled.field] = filled.message
        }
      })
    }

    const ANAMNESIS_DATA = {
      mainComplaint: form.mainComplaint,
      physicalExam: form.physicalExam,
      diagnosis: form.diagnosis,
      treatment: form.treatment,
      observation: form.observation,
      returnObservation: form.returnObservation
    }

    const REQUEST_DATA = {
      isEditing: {
        ...ANAMNESIS_DATA,
        id: appointment?.id
      },
      isCreating: {
        ...ANAMNESIS_DATA,
        date: moment(date).format('DD/MM/yyyy'),
        partnerId: partnerId,
        petId: petId,
        professionalId: professionalId
      }
    }

    const APPOINTMENT_ACTION = {
      isCreating: async () => {
        const { id } = await createAppointment(REQUEST_DATA[appointmentStatus])
        if (id) {
          const params = `?appointmentId=${id}&success=true`
          setShowSuccess(true)
          goToPage(URLS.appointment, params)
        }
      },
      isEditing: async () => {
        await updateAppointment(REQUEST_DATA[appointmentStatus])
        setShowSuccess(true)
        setInitialFields(ANAMNESIS_DATA)
      }
    }

    if (!_.isEmpty(errorsList)) {
      setErrors(errorsList)
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    try {
      APPOINTMENT_ACTION[appointmentStatus]()
    } catch (err) {
      console.error(err)
    }
  }

  const handleDeleteAppointment = async () => {
    try {
      await destroyAppointment(appointmentId)
      goToPage(`${URLS.petDetail}/${petId}`, '?alertContent=CANCELED')
    } catch (err) {
      console.error(err)
    }
  }

  const handleDiscardFormAction = (event, isBackPage) => {
    event.preventDefault()

    const hasBeenTouched = Object.keys(watchedFields).some((key) => {
      return watchedFields[key] !== initialFields[key]
    })

    const redirect = () => {
      return (isBackPage && !success)
        ? history.goBack()
        : goToPage(`${URLS.petDetail}/${petId}`)
    }

    if (isBackPage || !isEditing) {
      if (!hasBeenTouched) {
        return redirect()
      }
      CancelModal({
        ...MODAL_WARNINGS.LEAVE_APPOINTMENT,
        action: (action) => {
          if (action.isConfirmed) {
            redirect()
          }
        }
      })
      return
    }

    if (isEditing) {
      CancelModal({
        ...MODAL_WARNINGS.DELETE_APPOINTMENT,
        action: (action) => {
          if (action.isConfirmed) {
            handleDeleteAppointment(appointmentId)
          }
        }
      })
    }
  }

  return (
    <>
      <SideMenuTemplate>
        <S.MainContainer>
          <S.Main>
            <BackButton onClick={(e) => handleDiscardFormAction(e, true)} noMargin marginBottom='0' />
            <S.TitleContainer>
              <S.IconRound>
                <StethoscopeIcon />
              </S.IconRound>
              <div>
                <S.Title>
                  Ficha da consulta
                </S.Title>
                <S.Subtitle>
                  Registre todas as informações necessárias sobre a consulta e retorno do pet.
                </S.Subtitle>
              </div>
            </S.TitleContainer>
            <Card full loading={loadingPet} skeletonHeight={76} padding={'16px 24px;'}>
              <PetInfo isAppointment isEditable data={pet} loadingPet={loadingPet} updated={setPetWasUpdated} />
            </Card>
            {!loadingAppointment && <S.CaseHistory>
              <S.FormMeta isTitle>
                <strong>Anamnese</strong>
              </S.FormMeta>
              { appointment?.professional &&
                <S.FormMeta>
                  <strong>Veterinário que realizou a consulta: </strong>
                  {appointment.professional?.name}
                </S.FormMeta>
              }
              { appointment?.date &&
                <S.FormMeta>
                  <strong>Data que realizou a consulta: </strong>
                  {moment(appointment.date).format('DD/MM/YYYY')}
                </S.FormMeta>
              }
              <S.Form onSubmit={handleSubmit(handleSubmitForm)} autoComplete='off'>
                { !isEditing &&
                  <S.Row>
                    <S.DatepickerContainer>
                      <DatePicker
                        invertIcon
                        selected={date}
                        onChange={value => setDate(value)}
                        errorMessage={errors.date}
                        name='date'
                        placeholderText='DD/MM/AAAA'
                        dateFormat='dd/MM/yyyy'
                        locale='ptBR'
                        disabledKeyboardNavigation
                        showIcon
                        iconPosition='right'
                        label='Data da consulta (obrigatório)'
                        className='form-control'
                        vetView={true} />
                    </S.DatepickerContainer>
                  </S.Row>
                }
                {
                  ANAMNESIS_FIELDS.length > 0 && ANAMNESIS_FIELDS.map(
                    (field) => {
                      return (
                        <React.Fragment key={field.name}>
                          { field.title &&
                            <S.FieldTitle>{field.title}</S.FieldTitle>
                          }
                          <S.Row>
                            <Textarea
                              name={field.name}
                              label={field.label}
                              placeholder={field.placeholder}
                              height={176}
                              value={watchedFields[field.name]}
                              hookForm={register(field.name)}
                              isReadOnly={isEditingSameProfessional}
                            />
                          </S.Row>
                        </React.Fragment>
                      )
                    }
                  )
                }

                <S.AlertContainer>
                  <Alert
                    show={showSuccess}
                    type='success'
                    customIcon={Success}
                    onClose={() => setShowSuccess(false)}
                  >
                  A ficha de consulta foi salva com sucesso!
                  </Alert>
                </S.AlertContainer>

                <S.FormActions>
                  <S.CancelAppointmentButton disabled={isEditingSameProfessional} onClick={(e) => handleDiscardFormAction(e)}>{isEditing ? 'Descartar ficha da consulta' : 'Cancelar'}</S.CancelAppointmentButton>
                  <S.SaveAppointmentButton disabled={isEditingSameProfessional} type='submit'>{isEditing ? 'Salvar alterações' : 'Salvar ficha da consulta'}</S.SaveAppointmentButton>
                </S.FormActions>
              </S.Form>

            </S.CaseHistory>}
          </S.Main>
        </S.MainContainer>
      </SideMenuTemplate>
    </>
  )
}

export default Appointment
